import { useEffect, useRef, useState } from 'react'
import { Check, Download, Upload } from 'lucide-react'
import type { ItemSpec, OptionGroup, PersonalizationRow, Product, ProductOptions } from '@/types'
import { Field, Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { useToast } from '@/components/ui/toast'
import { activeGroups, addonAmount, addonLabel, offeredValues, requiredKeys, skippedKeys, useOptionGroups, withoutSkipped } from '@/lib/options'
import { downloadNameTemplate, parseNameFile } from '@/lib/personalizationExcel'
import { cn } from '@/lib/cn'
import { money } from '@/lib/money'

/**
 * Garment option pickers, one per Product Options group the product offers,
 * plus the jersey name & number personalization grid. Mirrors the client's
 * Excel job sheet (collar/sleeve/material/neck colour/piping + NAME1/NO1 blocks).
 * Groups, their order and their pictures come from admin > Products > Product
 * Options: a picture group (collars) is a grid of photo cards in its own section, the
 * rest are dropdowns in Details beside the free-text fields.
 *
 * Rendered inside the configurator card's sectioned form, so every group
 * closes with the shared hairline divider (border-b pb-5 mb-5).
 * The storefront product page renders its own "Remarks" textarea, so it
 * passes showRemark={false}; the admin create-order screen keeps the inline
 * remark field.
 *
 * With `onImport` the name grid also offers Download template / Upload Excel
 * (lib/personalizationExcel): the uploaded list sets the size quantities and
 * every row, and the parent applies both.
 */
export default function SpecFields({
  product,
  spec,
  onSpec,
  personalizations,
  onPersonalizations,
  personalizationOn,
  onPersonalizationToggle,
  onImport,
  showRemark = true,
  tierId,
}: {
  product: Pick<Product, 'name' | 'options' | 'sizes'>
  spec: ItemSpec
  onSpec: (spec: ItemSpec) => void
  personalizations: PersonalizationRow[]
  onPersonalizations: (rows: PersonalizationRow[]) => void
  /** Yes/No choice - rows only render (and are only compulsory) when on. */
  personalizationOn: boolean
  onPersonalizationToggle: (on: boolean) => void
  /** Offers the Excel template and upload; receives the file's size quantities and rows. */
  onImport?: (result: { sizeMatrix: Record<string, number>; rows: PersonalizationRow[] }) => void
  showRemark?: boolean
  /** The tier whose add-ons the option labels show, on payloads that hold them per tier (the manual order form). */
  tierId?: number | null
}) {
  const toast = useToast()
  const fileInput = useRef<HTMLInputElement>(null)
  const options: ProductOptions = product.options ?? {}
  const addons = options.option_addons
  const optionGroups = useOptionGroups().data
  // A choice can make another group unnecessary (Muslimah skips Sleeve type):
  // that group stays in its place greyed out, saying why, and setChoice
  // clears what it held. Hiding it outright made the field vanish under a
  // customer who had just filled it in.
  const skipped = skippedKeys(spec, optionGroups)
  // Only the visible groups this product offers something in.
  const groups = activeGroups(optionGroups).filter((group) => offeredValues(options, group).length > 0)
  // "Not needed for MUSLIMAH": the chosen value that skips this group.
  const skipNote = (group: OptionGroup): string | null => {
    if (!skipped.includes(group.key)) return null
    const cause = activeGroups(optionGroups)
      .map((other) => other.values.find((value) => value.value === spec[other.spec_key]))
      .find((value) => value?.skips?.includes(group.key))
    return `Not needed for ${cause?.value ?? 'this choice'}`
  }
  const setChoice = (group: OptionGroup, value: string | undefined) =>
    onSpec(withoutSkipped({ ...spec, [group.spec_key]: value }, optionGroups))
  // Compulsory unless switched off for this product; the asterisk says so.
  const required = requiredKeys(options)
  const pictureGroups = groups.filter((group) => group.display === 'picture')
  const selectGroups = groups.filter((group) => group.display !== 'picture')

  const setRow = (index: number, patch: Partial<PersonalizationRow>) => {
    onPersonalizations(personalizations.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  const importFile = async (file: File | undefined) => {
    if (!file || !onImport) return
    const result = await parseNameFile(file, product.sizes)
    if ('error' in result) {
      toast.error('List not loaded', result.error)
      return
    }
    onImport({ sizeMatrix: result.sizeMatrix, rows: result.rows })
    const perSize = Object.entries(result.sizeMatrix)
      .map(([size, qty]) => `${size} ${qty}`)
      .join(', ')
    toast.success(`${result.rows.length} pieces loaded`, `Size quantities set from the file: ${perSize}.`)
    if (result.incomplete.length > 0) {
      toast.info('Some names or numbers are blank', `Fill them in below (Excel row ${result.incomplete.slice(0, 5).join(', ')}${result.incomplete.length > 5 ? '...' : ''}).`)
    }
  }

  return (
    <div className="mt-5">
      {pictureGroups.map((group) => {
        const pictures = new Map(group.values.map((value) => [value.value, value.image_path]))
        const note = skipNote(group)
        if (note) {
          return (
            <section key={group.key} className="mb-5 border-b border-foreground/10 pb-5">
              <p className="text-base font-bold text-foreground/40">{group.label}</p>
              <p className="mt-3 rounded-lg border border-dashed border-foreground/15 px-3 py-2.5 text-sm text-foreground/50">{note}</p>
            </section>
          )
        }
        return (
          <section key={group.key} className="mb-5 border-b border-foreground/10 pb-5">
            <p className="text-base font-bold">
              {group.label}
              {required.includes(group.key) && !skipNote(group) && (
                <span className="ml-1 text-danger" aria-hidden="true">
                  *
                </span>
              )}
            </p>
            {/* Cards keep a set width and the row fits as many as there is room for, so the full-width manual order form shows more of them rather than bigger ones. */}
            <div className="mt-3 grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-2 sm:grid-cols-[repeat(auto-fill,minmax(11rem,1fr))]">
              {offeredValues(options, group).map((value) => {
                const selected = spec[group.spec_key] === value
                return (
                  <ChoiceCard
                    key={value}
                    image={pictures.get(value)}
                    label={value}
                    addon={addonAmount(addons?.[group.key]?.[value], tierId)}
                    selected={selected}
                    onClick={() => setChoice(group, selected ? undefined : value)}
                  />
                )
              })}
            </div>
          </section>
        )
      })}

      <section className="mb-5 border-b border-foreground/10 pb-5">
        <p className="text-base font-bold">Details</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {selectGroups.map((group) => (
            <Field key={group.key} label={group.label} required={required.includes(group.key) && !skipNote(group)}>
              <Select
                value={skipNote(group) ? '' : (spec[group.spec_key] ?? '')}
                onChange={(v) => setChoice(group, v || undefined)}
                disabled={!!skipNote(group)}
                placeholder={skipNote(group) ? 'Not needed' : 'Select...'}
                options={offeredValues(options, group).map((value) => ({
                  value,
                  label: addonLabel(value, addonAmount(addons?.[group.key]?.[value], tierId)),
                }))}
              />
              {/* The reason under the field: the box is too narrow to say it. */}
              {skipNote(group) && <p className="text-xs text-foreground/50">{skipNote(group)}</p>}
            </Field>
          ))}
          <Field label="Neck colour">
            <Input value={spec.neck_color ?? ''} onChange={(e) => onSpec({ ...spec, neck_color: e.target.value || undefined })} placeholder="e.g. BLACK" />
          </Field>
          <Field label="Piping / backtape">
            <Input value={spec.piping ?? ''} onChange={(e) => onSpec({ ...spec, piping: e.target.value || undefined })} placeholder="optional" />
          </Field>
          {showRemark && (
            <Field label="Remark">
              <Input value={spec.remark ?? ''} onChange={(e) => onSpec({ ...spec, remark: e.target.value || undefined })} placeholder="optional" />
            </Field>
          )}
        </div>
      </section>

      {options.allow_personalization && (
        <section className="mb-5 border-b border-foreground/10 pb-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-base font-bold">
              Name &amp; number
              {(options.personalization_price ?? 0) > 0 && (
                <span className="ml-1.5 text-sm font-semibold text-primary">
                  (+RM{options.personalization_price!.toFixed(2)}/pc)
                </span>
              )}
            </p>
            <div className="flex gap-1.5">
              {([false, true] as const).map((on) => (
                <button
                  key={String(on)}
                  type="button"
                  onClick={() => onPersonalizationToggle(on)}
                  className={cn(
                    'rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors',
                    personalizationOn === on
                      ? 'border-transparent bg-foreground/5 font-bold text-foreground'
                      : 'border-foreground/15 text-foreground/60 hover:text-foreground',
                  )}
                >
                  {on ? 'Yes' : 'No'}
                </button>
              ))}
            </div>
          </div>
          {personalizationOn && onImport && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => downloadNameTemplate(product.name, product.sizes, personalizations)}
                className="inline-flex items-center gap-1.5 rounded-full border border-foreground/15 px-3.5 py-1.5 text-xs font-semibold text-foreground/70 transition-colors hover:text-foreground"
              >
                <Download className="size-3.5" />
                Download template
              </button>
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-full border border-foreground/15 px-3.5 py-1.5 text-xs font-semibold text-foreground/70 transition-colors hover:text-foreground"
              >
                <Upload className="size-3.5" />
                Upload Excel
              </button>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => {
                  void importFile(e.target.files?.[0])
                  // Cleared so picking the same file again re-imports it.
                  e.target.value = ''
                }}
              />
              <span className="text-2xs text-foreground/50">One row per piece; the file sets the size quantities.</span>
            </div>
          )}
          {personalizationOn &&
            (personalizations.length === 0 ? (
              <p className="mt-2 text-xs text-foreground/50">
                {onImport
                  ? 'Choose your size quantities above, or upload the Excel - one row per piece will appear here.'
                  : 'Choose your size quantities above - one row per piece will appear here.'}
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                <p className="text-xs text-foreground/60">
                  One row per piece - name and number are required for every piece
                  {(options.personalization_price ?? 0) > 0 &&
                    `, RM${options.personalization_price!.toFixed(2)} per piece is added`}
                  .
                </p>
                {personalizations.map((row, i) => (
                  // One line at sm+; below sm a two-line grid (size chip + no,
                  // then the name full-width) so 375px never overflows.
                  <div key={i} className="grid grid-cols-[3.5rem_1fr] items-center gap-2 sm:flex">
                    <span className="rounded-lg bg-foreground/5 px-2 py-2 text-center text-xs font-bold uppercase sm:w-28">
                      {row.size}
                    </span>
                    <Input
                      className="order-last col-span-full sm:order-none"
                      placeholder={`Name ${i + 1}`}
                      value={row.name}
                      onChange={(e) => setRow(i, { name: e.target.value })}
                    />
                    <Input className="sm:w-24" placeholder={`No ${i + 1}`} value={row.number} onChange={(e) => setRow(i, { number: e.target.value })} />
                  </div>
                ))}
              </div>
            ))}
        </section>
      )}
    </div>
  )
}

/**
 * One picture-group choice, Printscape style (Duncan 2026-10-21): the photo on
 * top with the add-on as a frosted pill on it, the label and a check below.
 * A value without a picture is a text card in the same grid, its add-on on a
 * second line so it does not steal the label's width.
 */
function ChoiceCard({
  image,
  label,
  addon,
  selected,
  onClick,
}: {
  image?: string | null
  label: string
  addon: number
  selected: boolean
  onClick: () => void
}) {
  const [broken, setBroken] = useState(false)
  const fill = usePhotoFill(image)
  const photo = image && !broken
  return (
    // The wrapper holds the hover preview, outside the button's overflow-hidden.
    <div className="group relative flex min-w-0">
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        title={photo ? undefined : label}
        className={cn(
          'relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-white text-left transition-colors',
          selected ? 'border-primary' : 'border-foreground/15 hover:border-primary/40',
        )}
      >
        {photo && (
          <div className="relative shrink-0 bg-white">
            {/* A photo fills the frame, as on Printscape; a drawing on white is shown whole so its title is not cropped. */}
            <img
              src={image}
              alt=""
              loading="lazy"
              decoding="async"
              className={cn('aspect-video w-full', fill ? 'object-cover' : 'object-contain')}
              onError={() => setBroken(true)}
            />
            {addon > 0 && (
              <span className="absolute bottom-1.5 right-1.5 rounded-full bg-white/90 px-2.5 py-0.5 text-2xs font-semibold text-foreground shadow-sm backdrop-blur-sm">
                +{money(addon)}
              </span>
            )}
          </div>
        )}
        {/* flex-1 + items-center: the grid stretches every card in a row to its tallest, so a one-line label centres. */}
        <div className={cn('flex flex-1 items-center justify-between gap-1.5 px-2', photo ? 'py-1.5' : 'py-2.5')}>
          <span className="min-w-0">
            <span className="line-clamp-2 break-words text-sm font-medium leading-5 text-foreground">{label}</span>
            {!photo && addon > 0 && <span className="mt-0.5 block text-xs text-foreground/60">+{money(addon)}</span>}
          </span>
          {selected && (
            <span className="grid size-5 shrink-0 place-items-center rounded-full bg-primary text-white">
              <Check className="size-3.5" />
            </span>
          )}
        </div>
      </button>
      {/* Hover-only whole picture (back by Duncan's request 2026-10-25), uncropped since the card crops it: below sm touch has no hover and it would overflow the column. */}
      {photo && (
        <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 hidden w-max -translate-x-1/2 flex-col items-center gap-1 rounded-lg border border-foreground/15 bg-white p-2 shadow-lg sm:group-hover:flex">
          <img src={image} alt="" className="size-36 rounded object-contain" />
          <span className="max-w-36 text-center text-2xs font-medium text-foreground">{label}</span>
        </span>
      )}
    </div>
  )
}

/**
 * Whether an option picture should fill its card (Duncan 2026-10-25). The
 * collar drawings and a square fabric swatch are the same shape, so the shape
 * cannot tell them apart; the corners can: a drawing sits on white or
 * transparent, a photo runs to the edge. Sampled from a separate fetch so the
 * visible <img> is never put at risk, and any failure keeps the safe
 * whole-picture view.
 *
 * cache: 'no-store' matters. R2 answers the <img>'s plain request without a
 * CORS header and marks it immutable for a year, and a CORS load of the same
 * URL is then served that cached copy and blocked, so a crossOrigin Image
 * probe silently failed for every picture already shown once.
 */
function usePhotoFill(src?: string | null): boolean {
  const [fill, setFill] = useState(false)
  useEffect(() => {
    setFill(false)
    if (!src) return
    let live = true
    void (async () => {
      try {
        const response = await fetch(src, { mode: 'cors', cache: 'no-store' })
        if (!response.ok) return
        const size = 16
        const bitmap = await createImageBitmap(await response.blob(), { resizeWidth: size, resizeHeight: size })
        const canvas = document.createElement('canvas')
        canvas.width = canvas.height = size
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctx.drawImage(bitmap, 0, 0)
        bitmap.close()
        const { data } = ctx.getImageData(0, 0, size, size)
        const blank = [0, size - 1, size * (size - 1), size * size - 1].every((pixel) => {
          const [r, g, b, a] = data.slice(pixel * 4, pixel * 4 + 4)
          return a < 32 || (r > 235 && g > 235 && b > 235)
        })
        if (live) setFill(!blank)
      } catch {
        // No CORS, a network error or an undecodable file: keep the whole picture.
      }
    })()
    return () => {
      live = false
    }
  }, [src])
  return fill
}
